/**
 * The box every support message is written in: a new query, a reply from
 * the person who asked, a reply from an admin. One text box, one screenshot
 * slot, one send button. Ctrl or Cmd + Enter sends.
 */

import { Camera, Send, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import type { MessageDraft } from "@/lib/api/support";
import { TextareaField } from "@/primitives/forms/TextareaField";
import { Button } from "@/primitives/ui/Button";

import { ScreenshotPicker } from "./ScreenshotPicker";

export interface SupportComposerProps {
  placeholder: string;
  submitLabel?: string;
  /** Resolve to clear the box. Throw to keep what was typed. */
  onSubmit: (draft: MessageDraft) => Promise<void>;
  /** The floating panel steps aside while a box is being dragged. */
  onPickingChange?: (picking: boolean) => void;
  autoFocus?: boolean;
  minHeight?: number;
}

export function SupportComposer({
  placeholder,
  submitLabel = "Send",
  onSubmit,
  onPickingChange,
  autoFocus,
  minHeight = 88,
}: SupportComposerProps) {
  const [body, setBody] = useState("");
  const [shot, setShot] = useState<Blob | null>(null);
  const [picking, setPicking] = useState(false);
  const [sending, setSending] = useState(false);
  const preview = useMemo(() => (shot ? URL.createObjectURL(shot) : null), [shot]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const setPick = (value: boolean) => {
    setPicking(value);
    onPickingChange?.(value);
  };

  const ready = (body.trim().length > 0 || shot !== null) && !sending;

  const send = async () => {
    if (!ready) return;
    setSending(true);
    try {
      await onSubmit({ body: body.trim(), screenshot: shot });
      setBody("");
      setShot(null);
    } catch {
      // The caller has already said what went wrong. Keep the words.
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="sq-compose">
      <TextareaField
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        minHeight={minHeight}
        maxHeight={200}
        maxLength={5000}
        autoFocus={autoFocus}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void send();
          }
        }}
      />

      {preview ? (
        <div className="sq-compose__shot">
          <img src={preview} alt="Screenshot to send" />
          <span>Screenshot added</span>
          <button type="button" className="sq-icon-btn" aria-label="Remove the screenshot" onClick={() => setShot(null)}>
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      ) : null}

      <div className="sq-compose__bar">
        <Button type="button" variant="ghost" size="sm" leadingIcon={Camera} onClick={() => setPick(true)} disabled={sending}>
          {shot ? "Retake" : "Screenshot"}
        </Button>
        <Button type="button" variant="primary" size="sm" leadingIcon={Send} onClick={() => void send()} disabled={!ready} loading={sending}>
          {submitLabel}
        </Button>
      </div>

      {picking ? (
        <ScreenshotPicker
          onCancel={() => setPick(false)}
          onCapture={(blob) => {
            setShot(blob);
            setPick(false);
          }}
        />
      ) : null}
    </div>
  );
}
